import { AsyncCheckoutOrderType } from "@/components/Checkout/types";

import {
  brandclub_colors,
  Button,
  formatMoney,
  getFeaturedRetailerImage,
  getRetailerFromId,
} from "@brandclub/common-ui";

import { styled, useTheme } from "@mui/material/styles";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";

import {
  saveRetailerCredentials,
  updateOrderType,
} from "@/api/rest/checkoutApi";
import { useEffect, useRef, useState } from "react";
import { useCheckoutRetailer } from "../../../hooks/useCheckoutRetailer";
import EditConnectPopup, {
  PopupStatus,
} from "../retailerConnect/EditConnectPopup";
import { useInjectRetailerCredentials } from "./RetailerConnect";

const CheckoutOptionsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 18,
  paddingTop: 4,
}));

const getUserAccountDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedUserAccountOrderShippingFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedUserAccountOrderShippingFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";
  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(rewardAmount)} in cash rewards`;
  } else {
    line1Text = `Earn cash rewards and member-only exclusives`;
  }

  if (connectRewardAmount) {
    line1Highlight = `get ${formatMoney(connectRewardAmount)} in bonus rewards`;
  }

  if (!estimatedUserAccountOrderShippingFees) {
    line2Text = `Get fast, free shipping`;
  } else {
    line2Text = `Pay ${formatMoney(
      estimatedUserAccountOrderShippingFees
    )} in estimated shipping and service fees`;
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Highlight = `get ${formatMoney(
      estimatedGuestOrderTotal - estimatedUserAccountOrderTotal
    )} in estimated savings`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const getGuestDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedGuestOrderShippingFees,
  estimatedGuestServiceFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedGuestOrderShippingFees: number;
  estimatedGuestServiceFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";

  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(
      rewardAmount - (connectRewardAmount ?? 0)
    )} in cash rewards`;
  } else {
    line1Text = "";
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Text = `Pay ${formatMoney(
      estimatedGuestOrderShippingFees + estimatedGuestServiceFees
    )} in estimated shipping and service fees`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const AccountOptionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",

  border: `1px solid ${brandclub_colors.lightGray}`,
  transition: "all 0.3s ease",
  borderRadius: 8,
  "&.selected": {
    borderColor: theme.vars.palette.text.primary,
  },
  ".display-text": {
    display: "inline",
  },
  color: theme.vars.palette.text.primary,
  overflow: "hidden",
  ".desktop": {
    display: "flex",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  ".mobile": {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },

  ".account-option-head": {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,

    padding: "20px 20px",
    backgroundColor: brandclub_colors.blueWhite,
    borderBottom: `1px solid ${brandclub_colors.lightGray}`,
    [theme.breakpoints.down("md")]: {
      padding: "10px 12px",
      alignItems: "flex-start",
    },
  },

  ".account-option-head-title-container": {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    width: "fit-content",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "space-between",
    },
  },

  ".account-option-head-title": {
    fontSize: 20,
    cursor: "pointer",
    fontWeight: 600,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    ".account-option-head-title-logo": {
      width: 32,
      height: 32,
      borderRadius: 999,
      objectFit: "cover",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  },

  ".option-head-checkbox": {
    width: 20,
    cursor: "pointer",
    height: 20,
    borderRadius: 999,
    border: `1px solid ${brandclub_colors.lightGray}`,
    backgroundColor: brandclub_colors.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      marginTop: 2,
      width: 16,
      height: 16,
    },

    " .option-head-checkbox-selected": {
      backgroundColor: theme.vars.palette.text.primary,
      width: 10,
      height: 10,
      borderRadius: 999,
      [theme.breakpoints.down("md")]: {
        width: 8,
        height: 8,
      },
    },
  },
  ".account-option-body": {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "24px 50px 5px 50px",
    fontSize: 14,
    [theme.breakpoints.down("md")]: {
      padding: "15px 35px 15px 35px",
      fontSize: 12,
    },
    ".account-option-body-line1": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      "&.username": {
        [theme.breakpoints.down("md")]: {
          marginBottom: 0,
        },
      },
    },
    ".account-option-body-line2": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
  },

  ".account-option-button": {
    marginTop: 10,
    width: "fit-content",
    padding: "0 50px 25px 50px",
    boxSizing: "border-box",

    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      marginTop: 0,
      width: "100%",
      padding: "0 20px 15px 20px",
      boxSizing: "border-box",
    },
  },
  ".edit-button": {
    justifyContent: "center",
    alignItems: "center",
  },
}));

const Ribbon = styled("div")(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.vars.palette.text.primary,
  "--r": ".8em",
  backgroundColor: theme.vars.palette.secondary.main,
  textTransform: "uppercase",

  paddingInline: "calc(var(--r) + 1.5em)",
  lineHeight: 2,
  clipPath:
    "polygon(0 0,100% 0,calc(100% - var(--r)) 50%,100% 100%,0 100%, var(--r) 50%)",
  // background: "#CC333F",
  width: "fit-content",
  [theme.breakpoints.down("md")]: {
    fontSize: 10,
    paddingInline: "calc(var(--r) + .7em)",
  },
}));

const AccountOption = ({
  selected = false,
  onClick,
  type,
}: {
  selected: boolean;
  onClick: () => void;
  type: AsyncCheckoutOrderType;
}) => {
  const theme = useTheme();
  const { currentRetailer } = useCheckoutRetailer();
  const [state, send] = useCheckoutUI();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;

  const [currentUsername, setCurrentUsername] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    username: existingUsername,
    password: existingPassword,
    loading,
  } = useInjectRetailerCredentials();

  useEffect(() => {
    // Set the default password to the decrypted password if it matches the hash
    if (!loading) {
      if (existingUsername) setCurrentUsername(existingUsername);
      if (existingPassword) setCurrentPassword(existingPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPassword, existingUsername, loading]);

  const hasRetailerConnection = !!checkoutIntent?.retailerConnection?.username;

  const { line1Text, line1Highlight, line2Text, line2Highlight } =
    getUserAccountDisplayText({
      rewardAmount: rewardAmounts.totalRewardAmount,
      connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
      estimatedGuestOrderTotal:
        checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
      estimatedUserAccountOrderTotal:
        checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
      estimatedUserAccountOrderShippingFees:
        checkoutIntent?.estimatedUserAccountOrderSummary?.shippingFees ?? 0,
    });

  const onSaveCredentials = async (username: string, password: string) => {
    setCurrentUsername(username);
    setCurrentPassword(password);
    try {
      const checkoutIntent = await saveRetailerCredentials({
        retailerId: currentRetailer?.retailerId?.toString() ?? "",
        checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
        credentials: {
          username,
          password,
        },
      });
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const openEdit = () => {
    EditConnectPopup.show({
      connectReward: rewardAmounts?.retailerConnectRewardAmount,
      onSaveCredentials: onSaveCredentials,
      currentUsername: currentUsername,
      currentPassword: currentPassword,
      defaultStatus: PopupStatus.WELCOME,
      retailer: currentRetailer,
    });
  };

  const title = `Checkout with your ${
    currentRetailer?.retailerId
      ? getRetailerFromId(currentRetailer?.retailerId)
      : "retailer"
  } perks`;

  const retailerCheckoutReward =
    rewardAmounts.totalRewardAmount +
    rewardAmounts.retailerConnectRewardAmount +
    (checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0) -
    (checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0);

  const signInButtonText = `Connect to get ${formatMoney(
    retailerCheckoutReward
  )} in perks`;
  return (
    <AccountOptionContainer className={selected ? "selected" : ""}>
      <div className="account-option-head">
        <div className="option-head-checkbox" onClick={onClick} role="button">
          {selected && <div className="option-head-checkbox-selected"></div>}
        </div>
        <div className="account-option-head-title-container">
          <div
            className="account-option-head-title"
            role="button"
            onClick={onClick}
          >
            {currentRetailer?.retailerId ? (
              <div className="account-option-head-title-logo desktop">
                <img
                  src={getFeaturedRetailerImage(currentRetailer?.retailerId)}
                  alt="logo"
                />
              </div>
            ) : null}
            <div className="account-option-head-title-text">{title}</div>
          </div>
          <Ribbon className="best-value-ribbon">BEST VALUE</Ribbon>
        </div>
      </div>

      <div className="account-option-body">
        {hasRetailerConnection ? (
          <div className="account-option-body-line1 username">
            {checkoutIntent?.retailerConnection?.username}
          </div>
        ) : null}
        <div className="account-option-body-line1">
          <div className="display-text">
            {line1Text}
            {line1Highlight ? (
              <span className="account-option-body-line1-highlight">
                {" "}
                - <strong>{line1Highlight}</strong>
              </span>
            ) : null}
          </div>
        </div>
        <div className="account-option-body-line2">
          <div className="display-text">
            {line2Text}
            {line2Highlight ? (
              <span className="account-option-body-line2-highlight">
                {" "}
                - <strong>{line2Highlight}</strong>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {hasRetailerConnection ? (
        <div className="account-option-button">
          <Button
            className="checkout-solid-button"
            variant={"solid"}
            onClick={openEdit}
            disableMaxWidth
            sx={() => ({
              width: "100%",
              padding: "0px 40px",

              [theme.breakpoints.down("md")]: {
                padding: "0px 20px",
              },
            })}
          >
            Edit{" "}
            {currentRetailer?.retailerId
              ? getRetailerFromId(currentRetailer?.retailerId)
              : "retailer"}{" "}
            details
          </Button>
        </div>
      ) : (
        <div className="account-option-button">
          <Button
            className="checkout-solid-button"
            variant={"solid"}
            onClick={openEdit}
            disableMaxWidth
            sx={() => ({
              width: "100%",
              padding: "0px 40px",

              [theme.breakpoints.down("md")]: {
                padding: "0px 20px",
              },
            })}
          >
            {signInButtonText}
          </Button>
        </div>
      )}
    </AccountOptionContainer>
  );
};

const OptionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${brandclub_colors.lightGray}`,
  borderRadius: 8,

  overflow: "hidden",
  transition: "all 0.3s ease",
  "&.selected": {
    borderColor: theme.vars.palette.text.primary,
  },

  ".display-text": {
    display: "inline",
  },

  ".mobile": {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },
  ".desktop": {
    display: "flex",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  ".option-head": {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,

    padding: "20px 20px",
    backgroundColor: brandclub_colors.blueWhite,
    borderBottom: `1px solid ${brandclub_colors.lightGray}`,
    [theme.breakpoints.down("md")]: {
      padding: "10px 12px",
    },

    ".option-head-checkbox": {
      width: 20,
      cursor: "pointer",
      height: 20,
      borderRadius: 999,
      border: `1px solid ${brandclub_colors.lightGray}`,
      backgroundColor: brandclub_colors.white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("md")]: {
        marginTop: 2,
        width: 16,
        height: 16,
      },

      " .option-head-checkbox-selected": {
        backgroundColor: theme.vars.palette.text.primary,
        width: 10,
        height: 10,
        borderRadius: 999,
        [theme.breakpoints.down("md")]: {
          width: 8,
          height: 8,
        },
      },
    },
    ".option-head-icon": {
      width: 32,
      height: 32,
      borderRadius: 999,
      objectFit: "cover",
      [theme.breakpoints.down("md")]: {
        width: 20,
        height: 20,
      },
    },
    ".option-head-title": {
      cursor: "pointer",
      fontSize: 20,
      flex: 1,
      fontWeight: 600,
      [theme.breakpoints.down("md")]: {
        fontSize: 14,
      },
    },
  },
  ".option-body": {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "24px 50px 24px 50px",
    fontSize: 14,
    [theme.breakpoints.down("md")]: {
      padding: "15px 20px 15px 20px",
      fontSize: 12,
    },

    ".option-body-line1": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
    ".option-body-line2": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
  },
}));

const GuestOption = ({
  selected = false,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) => {
  const [state] = useCheckoutUI();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;

  const title = `Checkout as a guest`;

  const { line1Text, line1Highlight, line2Text, line2Highlight } =
    getGuestDisplayText({
      rewardAmount: rewardAmounts.totalRewardAmount,
      connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
      estimatedGuestOrderTotal:
        checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
      estimatedUserAccountOrderTotal:
        checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
      estimatedGuestOrderShippingFees:
        checkoutIntent?.estimatedGuestOrderSummary?.shippingFees ?? 0,
      estimatedGuestServiceFees:
        checkoutIntent?.estimatedGuestOrderSummary?.serviceFee ?? 0,
    });

  return (
    <OptionContainer className={selected ? "selected" : ""}>
      <div className="option-head">
        <div className="option-head-checkbox" onClick={onClick} role="button">
          {selected && <div className="option-head-checkbox-selected"></div>}
        </div>
        <div>
          <div className="option-head-title" role="button" onClick={onClick}>
            {title}
          </div>
        </div>
      </div>
      <div className="option-body">
        <div className="option-body-line1">
          <div className="display-text">
            {line1Text}
            {line1Highlight ? (
              <span className="option-body-line1-highlight">
                {" "}
                - <strong>{line1Highlight}</strong>
              </span>
            ) : null}
          </div>
        </div>
        <div className="option-body-line2">
          <div className="display-text">
            {line2Text}
            {line2Highlight ? (
              <span className="option-body-line1-highlight">
                {" "}
                - <strong>{line2Highlight}</strong>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </OptionContainer>
  );
};

const CheckoutOptions = () => {
  const [machineState, send] = useCheckoutUI();

  const toggleOrderType = async (orderType: AsyncCheckoutOrderType) => {
    const { checkoutIntent } = await updateOrderType({
      orderType,
      checkoutIntentId: machineState.context.checkoutIntent?.checkoutIntentId!,
    });
    send({
      type: "SET_CHECKOUT_INTENT",
      data: { checkoutIntent },
    });
  };

  // @ts-ignore
  const { checkoutIntent } = machineState.context;

  const orderType = checkoutIntent?.orderType ?? AsyncCheckoutOrderType.REGULAR;

  const handleOrderTypeChange = (orderType: AsyncCheckoutOrderType) => {
    toggleOrderType(orderType);
  };

  return (
    <CheckoutOptionsContainer>
      <>
        <AccountOption
          selected={orderType === AsyncCheckoutOrderType.REGULAR}
          onClick={() => handleOrderTypeChange(AsyncCheckoutOrderType.REGULAR)}
          type={AsyncCheckoutOrderType.REGULAR}
        />
        <GuestOption
          selected={orderType === AsyncCheckoutOrderType.GUEST}
          onClick={() => handleOrderTypeChange(AsyncCheckoutOrderType.GUEST)}
        />
      </>
    </CheckoutOptionsContainer>
  );
};

export default CheckoutOptions;
